/**
 * Capability flags for the Effort row's controls that need a backend change.
 *
 * Each flag is OFF unless it is exactly the string `'true'`. It is read from
 * `window.__ENV` first and then from the build-time `process.env`, and it is read on every
 * call, so a test or a runtime bootstrap can change it without re-importing the module.
 *
 * Today a flag can only be turned on at build or dev time. `window.__ENV` is seeded by
 * `public/index.html` with the Supabase keys alone, and `entrypoint.sh` substitutes only
 * those, so a deployed container cannot flip it. Doing so takes a runtime key in that
 * bootstrap plus an `entrypoint.sh` substitution (the `src/lib/supabase.js` pattern), as a
 * separate change made when the backend ships.
 *
 * With a flag off, the row renders and sends exactly what it did before the control
 * existed: the new `fn_effort_correct` parameters are not a no-op to an older database,
 * they answer PGRST202 and fail the edit.
 */

/** A container-time placeholder that was never substituted counts as "not set". */
const isPlaceholder = (v) => typeof v === 'string' && v.startsWith('__RUNTIME_') && v.endsWith('__');

/**
 * Resolves one flag. The runtime value wins over the build-time one, unless it is empty
 * or an unsubstituted placeholder.
 *
 * @param {string|undefined} runtime The `window.__ENV` value.
 * @param {string|undefined} buildtime The `process.env` value.
 * @returns {boolean}
 */
const resolve = (runtime, buildtime) => {
  const value = runtime && !isPlaceholder(runtime) ? runtime : buildtime;
  return value === 'true';
};

const runtimeEnv = () => (typeof window !== 'undefined' && window.__ENV) || {};

/**
 * Whether the move controls are on: editable start time, the date control, the project
 * picker, the `⋯` menu's Change project / date / time items, the overlap check and the
 * `p_when` / `p_client_id` / `p_task_id` parameters of `fn_effort_correct`.
 *
 * `process.env.REACT_APP_EFFORT_MOVE` is written out in full so the build can inline it.
 *
 * @returns {boolean}
 */
export const isEffortMoveEnabled = () =>
  resolve(runtimeEnv().REACT_APP_EFFORT_MOVE, process.env.REACT_APP_EFFORT_MOVE);

/**
 * Whether Delete is on: the `⋯` menu's Delete item, its in-row confirmation and the
 * `fn_effort_delete` call.
 *
 * @returns {boolean}
 */
export const isEffortDeleteEnabled = () =>
  resolve(runtimeEnv().REACT_APP_EFFORT_DELETE, process.env.REACT_APP_EFFORT_DELETE);

/**
 * Whether Undo after a delete is on: the toast and the `fn_effort_restore` call. It has
 * meaning only with Delete on; without it the delete confirmation says it cannot be undone.
 *
 * @returns {boolean}
 */
export const isEffortUndoEnabled = () =>
  resolve(runtimeEnv().REACT_APP_EFFORT_UNDO, process.env.REACT_APP_EFFORT_UNDO);
